/* eslint-disable react/jsx-props-no-spreading */
import React, { FunctionComponent, useMemo } from "react";
import { styled, formFieldVerticalGap, Header5, inputLeftPadding, media, StyledFieldLabel } from "@iventis/styles";
import { v4 as uuid } from "uuid";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Autocomplete, TextField } from "@mui/material";

import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { CheckBoxStyled, MultipleValueTextInput } from "@iventis/components";
import { AdditionalDataFieldProperties } from "@iventis/types";
import { DataFieldsChips, DataFieldServicesContext, IDataFieldServices } from "@iventis/datafield-editor";
import { replaceItemInArray } from "@iventis/utilities";
import { convertDataFieldToEventBlockTypeDataField, updateScheduleDataFields } from "./edit-event-block-type-helpers";
import { ScheduleConfigDatafieldsAdditionalFormData } from "./schedule-config-datafields";

interface EditEventBlockTypeProps {
    eventBlockType: ScheduleConfigEventBlockType;
    eventBlockTypes: ScheduleConfigEventBlockType[];
    venueTypes: ScheduleConfigVenueLocationType[];
    dataFields: DataField[];
    title?: string;
    dcasEventBlock?: boolean;
    showCustomerIdentifier?: boolean;
    requiredSystemDataFields?: boolean;
    childEventBlockDataFields?: DataField[];
    editName?: boolean;
    onValueChange: (configChange: ScheduleConfigEventBlockType) => void;
    onDataFieldsChange: (dataField: DataField[]) => void;
}

export const EditEventBlockType: FunctionComponent<EditEventBlockTypeProps> = ({
    eventBlockType,
    venueTypes,
    title,
    dataFields,
    eventBlockTypes,
    dcasEventBlock = false,
    showCustomerIdentifier,
    childEventBlockDataFields,
    requiredSystemDataFields = false,
    editName = true,
    onValueChange,
    onDataFieldsChange,
}) => {
    const translate = useIventisTranslate();

    // Memos
    const selectedVenues = useMemo(() => venueTypes.filter((venue) => eventBlockType.allowedVenueTypes.includes(venue.id)), [eventBlockType.allowedVenueTypes]);

    const selectedRelativeEventBlocks = useMemo(() => eventBlockTypes.filter((eventBlock) => eventBlockType.allowedRelativeTypes.includes(eventBlock.id)), [
        eventBlockType.allowedRelativeTypes,
    ]);

    const eventBlockDataFields = useMemo(
        () =>
            // Get only the datafields for this event block
            // For each datafield also get the required and system datafield
            dataFields.reduce<AdditionalDataFieldProperties[]>((eventBlockDataFields, df) => {
                const eventBlockDataField = eventBlockType.dataFields.find(({ dataFieldId }) => dataFieldId === df.id);
                if (eventBlockDataField) {
                    return [
                        ...eventBlockDataFields,
                        { ...df, required: eventBlockDataField.required, systemDataField: eventBlockDataField.systemDataFieldType, namingOrder: eventBlockDataField.namingOrder },
                    ];
                }
                return eventBlockDataFields;
            }, []),
        [eventBlockType.dataFields]
    );

    const selectedAttirbutesForSummaryText = useMemo(
        () => (childEventBlockDataFields != null ? childEventBlockDataFields.filter(({ id }) => eventBlockType.childSummaryDataFieldIds.includes(id)) : null),
        [eventBlockType.childSummaryDataFieldIds, childEventBlockDataFields]
    );

    const selectedDatafieldFoCustomerIdentifier = useMemo(() => eventBlockDataFields.find((df) => df.id === eventBlockType.customerIdDataFieldId), [
        eventBlockType.customerIdDataFieldId,
        eventBlockDataFields,
    ]);

    const nonEditableDataFieldIds = useMemo(
        () => (requiredSystemDataFields ? eventBlockType.dataFields.filter((df) => df.systemDataFieldType != null).map((df) => df.dataFieldId) : []),
        [requiredSystemDataFields, eventBlockType.dataFields]
    );

    // Value change handlers
    const valueChange = <PropertyType extends keyof ScheduleConfigEventBlockType>(property: PropertyType, value: ScheduleConfigEventBlockType[PropertyType]) => {
        onValueChange({ ...eventBlockType, [property]: value });
    };

    const valueChangeFromMultiSelect = <TValue extends { id: string }>(selectedValues: TValue[], property: keyof ScheduleConfigEventBlockType) => {
        const ids = selectedValues.map(({ id }) => id);
        valueChange(property, ids);
    };

    const onEventBlockDataFieldsChange = (updatedDataFields: AdditionalDataFieldProperties[]) => {
        const parsedUpdatedDataFields = updatedDataFields.map((df) => ({ ...df, id: df.id ?? uuid(), customerIdentifierName: df.customerIdentifierName ?? df.name }));
        // Convert dataFields to eventBlockTypeDataFields
        const updatedEventBlockDataFields = parsedUpdatedDataFields.map((df, index) => convertDataFieldToEventBlockTypeDataField(df, updatedDataFields.length + index));
        // Update event block dataFields
        valueChange("dataFields", updatedEventBlockDataFields);
        // Update all schedule config dataFields
        const updatedScheduleDataFields = updateScheduleDataFields(parsedUpdatedDataFields, eventBlockDataFields, dataFields);
        onDataFieldsChange(updatedScheduleDataFields);
    };

    const dataFieldServices: IDataFieldServices = useMemo(
        () => ({
            dataFieldsService: {
                postDataField: async (dataField) => onEventBlockDataFieldsChange([...eventBlockDataFields, dataField]),
                putDataField: async (dataField) => onEventBlockDataFieldsChange(replaceItemInArray(eventBlockDataFields, dataField)),
                deleteDataField: async (dataFieldId) => onEventBlockDataFieldsChange(eventBlockDataFields.filter(({ id }) => id !== dataFieldId)),
                getDataFieldsForResource: async () => [],
                isDataFieldBeingUsed: async () => null,
            },
            dataFieldListItemsService: {
                postDataFieldListItem: async () => null,
                putDataFieldListItem: async () => null,
                deleteDataFieldListItem: async () => null,
                // List items are not edited here
                getDataFieldListItems: async () => [],
                getDataFieldListItem: async () => null,
            },
        }),
        [onEventBlockDataFieldsChange, eventBlockDataFields]
    );

    return (
        <Container>
            {title && <Header5>{title}</Header5>}
            <InputsContainer className="grid" data-testid="edit-event-block">
                <div>
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockTypeName)}</StyledFieldLabel>
                    <TextField value={eventBlockType.name} onChange={(event) => valueChange("name", event.target.value)} disabled={!editName} fullWidth />
                </div>
                <div className="first-col">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockApiSingle)}</StyledFieldLabel>
                    <TextField value={eventBlockType.apiNameSingular} onChange={(event) => valueChange("apiNameSingular", event.target.value)} disabled={!editName} fullWidth />
                </div>
                <div className="second-col">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockApiPlural)}</StyledFieldLabel>
                    <TextField value={eventBlockType.apiNamePlural} onChange={(event) => valueChange("apiNamePlural", event.target.value)} disabled={!editName} fullWidth />
                </div>
                <div className="first-col">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockAllowedVenueTypes)}</StyledFieldLabel>
                    <MultipleValueTextInput
                        values={venueTypes}
                        selectedValues={selectedVenues}
                        onChange={(value) => valueChangeFromMultiSelect(value, "allowedVenueTypes")}
                        getLabel={(venue: ScheduleConfigVenueLocationType) => venue.name}
                        sectionalMargin={false}
                    />
                </div>
                <div className="second-col">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockRelativeTypes)}</StyledFieldLabel>
                    <MultipleValueTextInput
                        values={eventBlockTypes}
                        selectedValues={selectedRelativeEventBlocks}
                        onChange={(value) => valueChangeFromMultiSelect(value, "allowedRelativeTypes")}
                        getLabel={(eventBlock: ScheduleConfigEventBlockType) => eventBlock.name}
                        sectionalMargin={false}
                    />
                </div>
                {childEventBlockDataFields != null && (
                    <div className="first-col">
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockSummaryText)}</StyledFieldLabel>
                        <MultipleValueTextInput
                            values={childEventBlockDataFields}
                            selectedValues={selectedAttirbutesForSummaryText}
                            onChange={(value) => valueChangeFromMultiSelect(value, "childSummaryDataFieldIds")}
                            getLabel={(dataField) => dataField.name}
                            sectionalMargin={false}
                        />
                    </div>
                )}
                {!dcasEventBlock && (
                    <div className="tickbox-container second-col">
                        <CheckBoxStyled checked={eventBlockType.dcasCreationAllowed} onChange={() => valueChange("dcasCreationAllowed", !eventBlockType.dcasCreationAllowed)} />
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockDCAS)}</StyledFieldLabel>
                    </div>
                )}
                <div className="full-row">
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.attributes)}</StyledFieldLabel>
                    <DataFieldServicesContext.Provider value={dataFieldServices}>
                        <DataFieldsChips
                            dataFields={eventBlockDataFields ?? []}
                            getProjectDataFields={() => Promise.resolve([])}
                            AdditionalAttributeDataForm={ScheduleConfigDatafieldsAdditionalFormData}
                            showListItems={false}
                            nonEditableDataFieldIds={nonEditableDataFieldIds}
                        />
                    </DataFieldServicesContext.Provider>
                </div>
                {showCustomerIdentifier && (
                    <div className="first-col" data-testid={`customer-identifier-${eventBlockType.id}`}>
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.eventBlockCustomerIdentifier)}</StyledFieldLabel>
                        <Autocomplete
                            multiple={false}
                            options={eventBlockDataFields}
                            value={selectedDatafieldFoCustomerIdentifier}
                            getOptionLabel={(option: AdditionalDataFieldProperties) => option.name}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(_, value: AdditionalDataFieldProperties) => valueChange("customerIdDataFieldId", value.id)}
                        />
                    </div>
                )}
            </InputsContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${formFieldVerticalGap};
`;

const InputsContainer = styled.div`
    display: grid;
    gap: ${inputLeftPadding};
    grid-template-columns: 1fr 1fr;

    .first-col {
        grid-column: 1;
    }

    .second-col {
        grid-column: 2;

        ${media.extraSmall} {
            grid-column: 1;
        }
    }

    .full-row {
        grid-column: 1/3;
    }

    ${media.extraSmall} {
        grid-template-columns: 1fr;
    }

    .tickbox-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        label {
            margin-bottom: 0px;
        }
    }
`;
